import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare,
  FolderKanban,
  GraduationCap,
  Package,
  Monitor,
  Workflow,
  Palette,
  Plus,
  Wrench,
  Calculator,
  Brain,
} from "lucide-react";
import { cn } from "@/lib/utils";

export const OS_NAV_IDS = [
  "chats",
  "projects",
  "universidad",
  "inventario",
  "monitor",
  "automations",
  "customizes",
] as const;

export type OsNavId = (typeof OS_NAV_IDS)[number];

type NavItem = { id: OsNavId; label: string; icon: typeof MessageSquare };

const WORK_ITEMS: NavItem[] = [
  { id: "chats", label: "Chats", icon: MessageSquare },
  { id: "projects", label: "Proyectos", icon: FolderKanban },
];

const LAB_ITEMS: NavItem[] = [
  { id: "universidad", label: "Universidad", icon: GraduationCap },
  { id: "inventario", label: "Inventario", icon: Package },
  { id: "monitor", label: "Monitor", icon: Monitor },
];

const SYSTEM_ITEMS: NavItem[] = [
  { id: "automations", label: "Automatizaciones", icon: Workflow },
  { id: "customizes", label: "Personalizar", icon: Palette },
];

export type OsSidebarProps = {
  active: string;
  onSelect: (id: string) => void;
  /** Extra content under the active section (chat list, project list, …). */
  extra?: ReactNode;
  onNewChat?: () => void;
};

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="px-2.5 pt-3 pb-1 text-[9px] uppercase tracking-[0.2em] text-muted-foreground/70 font-bold">
      {children}
    </p>
  );
}

function NavButton({
  item,
  active,
  onSelect,
}: {
  item: NavItem;
  active: string;
  onSelect: (id: string) => void;
}) {
  const isActive = active === item.id;
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(item.id)}
      className={cn(
        "flex items-center gap-2.5 w-full rounded-lg px-2.5 py-2 text-left text-xs transition-colors cursor-pointer",
        isActive
          ? "bg-primary/10 text-primary border border-primary/30 font-bold"
          : "text-muted-foreground hover:text-foreground hover:bg-muted/40 border border-transparent"
      )}
    >
      <Icon className={cn("w-3.5 h-3.5 shrink-0", isActive && "text-primary")} />
      <span>{item.label}</span>
    </button>
  );
}

/**
 * Personal OS sidebar (Gemini / Antigravity style): your chats, projects, lab —
 * not a duplicate of the global Taller/Herramientas header.
 */
export function OsSidebar({ active, onSelect, extra, onNewChat }: OsSidebarProps) {
  return (
    <nav className="flex flex-col h-full min-h-0 py-3 px-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] font-mono-tech">
      <div className="px-2.5 pr-9 pb-3 mb-1 border-b border-border/60 shrink-0">
        <p className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground font-bold">
          Tu espacio
        </p>
        <p className="text-[11px] text-foreground/85 mt-0.5 leading-snug">
          Chats, proyectos y laboratorio
        </p>
      </div>

      {onNewChat ? (
        <button
          type="button"
          onClick={onNewChat}
          className="mx-0.5 mb-2 flex items-center justify-center gap-2 rounded-lg border border-primary/35 bg-primary/10 px-2.5 py-2 text-xs font-bold text-primary hover:bg-primary/15 cursor-pointer shrink-0"
        >
          <Plus className="w-3.5 h-3.5" />
          Nuevo chat
        </button>
      ) : null}

      {/* Single scroll region — suite links included so nothing is clipped off-screen */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-subtle flex flex-col gap-0.5">
        <SectionLabel>Trabajo</SectionLabel>
        {WORK_ITEMS.map((item) => (
          <NavButton key={item.id} item={item} active={active} onSelect={onSelect} />
        ))}

        {(active === "chats" || active === "projects") && extra ? (
          <div className="mt-1 mb-2 border-t border-border/40 pt-2">{extra}</div>
        ) : null}

        <SectionLabel>Laboratorio</SectionLabel>
        {LAB_ITEMS.map((item) => (
          <NavButton key={item.id} item={item} active={active} onSelect={onSelect} />
        ))}

        {active !== "chats" && active !== "projects" && extra ? (
          <div className="mt-1 mb-2">{extra}</div>
        ) : null}

        <SectionLabel>Sistema</SectionLabel>
        {SYSTEM_ITEMS.map((item) => (
          <NavButton key={item.id} item={item} active={active} onSelect={onSelect} />
        ))}

        <div className="mt-3 mb-1 pt-2 border-t border-border/60 px-1 space-y-0.5">
          <p className="px-1.5 text-[8px] uppercase tracking-[0.18em] text-muted-foreground/60 font-bold mb-1">
            Suite (fuera del chat)
          </p>
          <Link
            to="/taller"
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[10px] text-muted-foreground/70 hover:text-foreground hover:bg-muted/30"
          >
            <Wrench className="w-3 h-3 shrink-0" />
            Taller
          </Link>
          <Link
            to="/herramientas"
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[10px] text-muted-foreground/70 hover:text-foreground hover:bg-muted/30"
          >
            <Calculator className="w-3 h-3 shrink-0" />
            Herramientas
          </Link>
          <Link
            to="/brain3d"
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[10px] text-muted-foreground/70 hover:text-foreground hover:bg-muted/30"
          >
            <Brain className="w-3 h-3 shrink-0" />
            Cerebro 3D
          </Link>
        </div>
      </div>
    </nav>
  );
}
